/**
 * lib/remittance.ts
 * ------------------------------------------------------------------
 * TELLING A SUPPLIER WHAT THE MONEY WAS FOR.
 *
 * A bulk payment arrives at the supplier's bank as one lump with one
 * reference. On their side that is an unallocated receipt: they know
 * the school paid P84,300 and not which of their eleven outstanding
 * invoices it settles. So they allocate it to the oldest, or to the
 * wrong one, and three weeks later they are chasing an invoice the
 * school paid and the school is proving it did.
 *
 * A REMITTANCE ADVICE IS WHAT PREVENTS THAT, and it is worth more to
 * the school than to the supplier: it is the document that ends the
 * argument before it starts, and it costs nothing but sending.
 *
 * THE SUPPLIER'S OWN REFERENCES, NOT THE SCHOOL'S. A line reading
 * "PV/2026/0184 — P4,500" means nothing to somebody who has never seen
 * the school's voucher numbering. What they can match is their own
 * invoice number and what the order was for, so both go on the advice
 * and the voucher number rides along as the school's reference.
 *
 * ONE ADVICE PER SUPPLIER, NEVER ONE PER RUN. The run is how the school
 * organises its own work. Sending a supplier a document listing ninety
 * payments to other people would be a data breach with a covering note.
 *
 * PURE. Vouchers in, advices out. No PDF, no Firestore, no React.
 * ------------------------------------------------------------------ */
import { round2 } from '@/lib/financeCore';
import type { Voucher } from '@/lib/vouchers';
import type { Supplier } from '@/lib/suppliers';

export interface RemittanceLine {
  /** The school's reference — recognisable when they ring about it. */
  voucherNo: string;
  /** THEIRS. What the supplier can actually match against their ledger. */
  supplierRef?: string;
  /** The order it was raised against, where there was one. */
  poNo?: string;
  description: string;
  /** The date the school paid it, not the date it was raised. */
  paidAt: string;
  amount: number;
}

export interface RemittanceAdvice {
  /** Registered suppliers have an id; a one-off payee has only a name. */
  supplierId?: string;
  supplierName: string;
  /** Where the advice should go. Absent means somebody must post it. */
  email?: string;
  lines: RemittanceLine[];
  total: number;
  /** What the supplier will see on their bank statement. */
  bankRef?: string;
  /** The day the money left. */
  valueDate?: string;
}

const nameKey = (s: string) => (s ?? '').trim().toLowerCase();

/**
 * Split a set of paid vouchers into one advice per payee.
 *
 * Sorted by total, descending — the same order the creditors report
 * uses, so somebody working down a list of advices to send is working
 * down the same list they were just looking at.
 */
export function buildRemittances(
  vouchers: Voucher[],
  suppliers: Supplier[],
  opts: { bankRef?: string; valueDate?: string } = {},
): RemittanceAdvice[] {
  const byId = new Map(suppliers.map((s) => [s.id, s]));
  const out = new Map<string, RemittanceAdvice>();

  for (const v of vouchers) {
    const key = v.supplierId ?? `name:${nameKey(v.payeeName)}`;
    const supplier = v.supplierId ? byId.get(v.supplierId) : undefined;

    let advice = out.get(key);
    if (!advice) {
      advice = {
        supplierId: v.supplierId,
        supplierName: supplier?.name ?? v.payeeName ?? 'Unnamed',
        ...(supplier?.contactEmail ? { email: supplier.contactEmail } : {}),
        lines: [],
        total: 0,
        ...(opts.bankRef ? { bankRef: opts.bankRef } : {}),
        ...(opts.valueDate ? { valueDate: opts.valueDate } : {}),
      };
      out.set(key, advice);
    }

    advice.lines.push({
      voucherNo: v.voucherNo,
      ...(v.supplierInvoiceNo ? { supplierRef: v.supplierInvoiceNo } : {}),
      ...(v.poNo ? { poNo: v.poNo } : {}),
      description: v.narration ?? '',
      paidAt: (v.paidAt ?? '').slice(0, 10),
      amount: Number(v.total) || 0,
    });
    advice.total = round2(advice.total + (Number(v.total) || 0));
  }

  const advices = [...out.values()];
  for (const a of advices) {
    a.lines.sort((x, y) => x.paidAt.localeCompare(y.paidAt) || x.voucherNo.localeCompare(y.voucherNo));
  }
  return advices.sort((a, b) => b.total - a.total || a.supplierName.localeCompare(b.supplierName));
}

/**
 * Only what was actually paid belongs on an advice.
 *
 * Sending one for a voucher that has not cleared tells a supplier their
 * money is on the way when the school has not sent it — which is worse
 * than saying nothing, because they stop chasing.
 */
export function remittableVouchers(vouchers: Voucher[]): Voucher[] {
  return vouchers.filter((v) => v.currentStatus === 'paid');
}

/** Which advices cannot be emailed, and therefore need somebody to act. */
export function advicesWithoutEmail(advices: RemittanceAdvice[]): RemittanceAdvice[] {
  return advices.filter((a) => !a.email);
}

/**
 * A line the supplier cannot match.
 *
 * Neither their invoice number nor an order number means the advice says
 * only "we paid you P4,500 for something", and the reconciliation it was
 * meant to save is still theirs to do by hand. Not an error — plenty of
 * legitimate payments have neither — but worth naming while there is
 * still a chance to add one.
 */
export function unmatchableLines(advice: RemittanceAdvice): RemittanceLine[] {
  return advice.lines.filter((l) => !l.supplierRef && !l.poNo);
}
